import React from "react";
// Custom Date Picker Input

// Default
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// React icons
import {
  MdOutlineCalendarMonth,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";

// Mui
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { formatDate } from "../../utils/helper";
import { UnifiedIcon } from "../../components/Icon/UnifiedIcon";
import { isValid } from "date-fns";

// Types
interface DatePickerInputProps {
  label?: string;
  format?: string;
  value?: Date | string | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  name?: string;
  width?: string;
  required?: boolean;
  showTimeSelect?: boolean;
  maxDate?: Date | null;
}

const DatePickerInput: React.FC<DatePickerInputProps> = ({
  label,
  format = "dd/mm/yyyy HH:mm",
  value = null,
  onChange,
  placeholder,
  name,
  width = "100%",
  required = false,
  showTimeSelect = true,
  maxDate,
}) => {
  const [yearMenuOpen, setYearMenuOpen] = React.useState(false);
  const [monthMenuOpen, setMonthMenuOpen] = React.useState(false);
  const newDate = value ? new Date(value) : null;

  const ValidDate = isValid(newDate) && newDate ? newDate : null;

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Generate years from current-50 to current+50, but hide future if maxDate is provided
  const currentYear = new Date().getFullYear();
  const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 50;
  const startYear = currentYear - 100; // Allow back to 100 years for birth dates
  const years = Array.from(
    { length: maxYear - startYear + 1 },
    (_, i) => startYear + i,
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: width,
        zIndex: 12,
      }}
    >
      <Typography variant="body1" sx={{ marginBottom: "10px" }}>
        {label} {required && <Typography component="span">*</Typography>}
      </Typography>
      <style>
        {`
          .react-datepicker {
            border: 1px solid #dcdcdc !important;
            box-shadow: 0 4px 14px rgba(0,0,0,0.1) !important;
            border-radius: 8px !important;
            font-family: inherit !important;
          }
          .react-datepicker-popper {
            z-index: 9999 !important;
          }
          .react-datepicker__header {
            background-color: white !important;
            border-bottom: 1px solid #f0f0f0 !important;
          }
          .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
            background-color: #1976d2 !important;
            color: white !important;
          }
        `}
      </style>
      <DatePicker
        selected={ValidDate}
        value={
          formatDate(
            ValidDate,
            showTimeSelect ? "DD/MM/yyyy HH:mm" : "DD/MM/yyyy",
          ) || undefined
        }
        onChange={onChange}
        name={name}
        showTimeSelect={showTimeSelect}
        maxDate={maxDate || undefined}
        // timeFormat="HH:mm"
        timeIntervals={1}
        // timeCaption="time"
        // dateFormat="dd/MM/yyyy h:mm aa"
        placeholderText={placeholder || "dd/mm/yyyy"}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            sx={{
              margin: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              size="small"
            >
              <UnifiedIcon icon={MdChevronLeft} fontSize="20px" />
            </IconButton>

            <Select
              value={date.getFullYear()}
              onChange={({ target: { value } }) => changeYear(Number(value))}
              size="small"
              open={yearMenuOpen}
              onOpen={() => setYearMenuOpen(true)}
              onClose={() => setYearMenuOpen(false)}
              MenuProps={{
                disablePortal: false,
                style: { zIndex: 10001 },
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                },
                onMouseDown: (e: React.MouseEvent) => {
                  e.stopPropagation();
                },
                onClose: () => {
                  setYearMenuOpen(false);
                },
                PaperProps: {
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                  },
                  onMouseDown: (e: React.MouseEvent) => {
                    e.stopPropagation();
                  },
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              sx={{
                height: 30,
                fontSize: "0.875rem",
                backgroundColor: "white",
                "& .MuiSelect-select": {
                  paddingTop: "4px",
                  paddingBottom: "4px",
                },
              }}
            >
              {years.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={months[date.getMonth()]}
              onChange={({ target: { value } }) =>
                changeMonth(months.indexOf(value))
              }
              size="small"
              open={monthMenuOpen}
              onOpen={() => setMonthMenuOpen(true)}
              onClose={() => setMonthMenuOpen(false)}
              MenuProps={{
                disablePortal: false,
                style: { zIndex: 10001 },
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                },
                onMouseDown: (e: React.MouseEvent) => {
                  e.stopPropagation();
                },
                onClose: () => {
                  setMonthMenuOpen(false);
                },
                PaperProps: {
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                  },
                  onMouseDown: (e: React.MouseEvent) => {
                    e.stopPropagation();
                  },
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              sx={{
                height: 30,
                fontSize: "0.875rem",
                backgroundColor: "white",
                "& .MuiSelect-select": {
                  paddingTop: "4px",
                  paddingBottom: "4px",
                },
              }}
            >
              {months
                .filter((_, index) => {
                  if (maxDate && date.getFullYear() === maxDate.getFullYear()) {
                    return index <= maxDate.getMonth();
                  }
                  return true;
                })
                .map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
            </Select>

            <IconButton
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              size="small"
            >
              <UnifiedIcon icon={MdChevronRight} fontSize="20px" />
            </IconButton>
          </Box>
        )}
        customInput={
          <TextField
            variant="outlined"
            fullWidth
            placeholder={placeholder || "dd/mm/yyyy"}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <UnifiedIcon
                    icon={MdOutlineCalendarMonth}
                    fontSize={"15px"}
                  />
                </IconButton>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#fcfcfc",
                "& fieldset": {
                  border: "1px solid #c3c3c3",
                },
                "&:hover fieldset": {
                  border: "2px solid #1976d2",
                },
                "&.Mui-focused fieldset": {
                  border: "2px solid #1976d2",
                },
                "& input": {
                  padding: "6px 10px",
                  color: "#181B25",
                  fontWeight: 400,
                  fontSize: 15,
                  fontFamily: "Figtree",
                  minHeight: 24,
                  "&::placeholder": {
                    color: "#B0B0B0",
                    opacity: 1,
                  },
                },
              },
            }}
          />
        }
        popperPlacement="bottom-start"
      />
    </Box>
  );
};

export default DatePickerInput;
